import * as katex from '@iktakahiro/markdown-it-katex'
import DOMPurify from 'dompurify'
import markdown, { type MarkdownIt } from 'markdown-it'
import footnote from 'markdown-it-footnote'
import inline from 'markdown-it-for-inline'
import multimdTable from 'markdown-it-multimd-table'

const normalizeMarkdownInput = (input: unknown): string => {
  if (typeof input === 'string') return input
  if (input === undefined || input === null) return ''
  return String(input)
}

function applyExternalLinkTarget(md: MarkdownIt) {
  md.use(
    inline,
    'url_new_win',
    'link_open',
    function (tokens: any[], idx: number) {
      const [, href] = tokens[idx].attrs.find(
        (attr: string[]) => attr[0] === 'href',
      )

      if (href && !href.startsWith('/') && !href.startsWith('#')) {
        tokens[idx].attrPush(['target', '_blank'])
        tokens[idx].attrPush(['rel', 'noopener noreferrer'])
      }
    },
  )
}

// Enable `breaks: true` so that single line breaks in the template / prompt text
// are preserved as visual line breaks in the rendered preview.
// html disabled by default — tenant/user content must not execute raw HTML.
export const md = markdown({ linkify: true, html: false, breaks: true })
  .use(applyExternalLinkTarget)
  .use(katex)
  .use(footnote)

/** Markdown without raw HTML — preferred for user-authored content. */
const mdSafe = markdown({ linkify: true, html: false, breaks: true })
  .use(applyExternalLinkTarget)
  .use(multimdTable, {
    multiline: true,
    rowspan: true,
    headerless: false,
  })

/** Staff/catalog markdown+HTML — always sanitize before v-html. */
const mdTrustedHtml = markdown({ linkify: true, html: true, breaks: true })
  .use(applyExternalLinkTarget)
  .use(katex)
  .use(footnote)

/** Allowlist for IX-authored question/catalog HTML (subtitle, tooltip, …). */
const TRUSTED_HTML_PURIFY = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'b',
    'i',
    'u',
    'ul',
    'ol',
    'li',
    'a',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'code',
    'pre',
    'span',
    'div',
    'hr',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'title', 'class'],
  ALLOW_DATA_ATTR: false,
} as const

export function renderMarkdown(text: unknown) {
  return md.render(normalizeMarkdownInput(text))
}

export function renderMarkdownInline(text: unknown) {
  return md.renderInline(normalizeMarkdownInput(text))
}

export function renderSafeMarkdown(text: unknown) {
  return mdSafe.render(normalizeMarkdownInput(text))
}

/**
 * Render IX/catalog content that may include HTML tags stored in the DB.
 * Sanitizes with DOMPurify — do not use for tenant-authored free text.
 */
export function renderTrustedMarkdown(text: unknown) {
  const html = mdTrustedHtml.render(normalizeMarkdownInput(text))
  return DOMPurify.sanitize(html, { ...TRUSTED_HTML_PURIFY })
}
